import React, { useState } from 'react';
import Timer from 'react-compound-timer';
import { useWindowScroll } from 'react-use';

/* eslint-disable react/prop-types */
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { useDeviceDetect3 } from 'utils/hooks/useDeviceQuery.hook';
import { Typography } from 'components/DesignSystemV2/Typography';
import { MONTH_NAME_FORMAT2 } from 'constant';
import moment from 'moment';
import { imageFitStyle } from 'style/css-style';
import { LazyImage } from 'components/LazyImage';
import { useKeenSlider } from 'keen-slider/react';
import {
  PlatformIcon,
  PromotionIcon,
  TrainingIcon
} from '../components/PromotionIcons';
import { RightArrowIcon } from 'components/LinearThinkingNews/Icons';
import {
  IconArrowChevronLeftArrowLeft,
  IconArrowChevronLeftArrowRight
} from 'components/Icon/SVGIcons';
import { AutoPlay } from 'plugins/AutoPlay';
import { desktopFirstV3 } from 'style/breakpointsv3';
import { toScreen } from 'utils/media-query/responsive.util';

const { Days, Hours, Minutes, Seconds } = Timer;

const transition = { duration: 0.3, ease: 'easeOut' };
const colorMapping = {
  purple: {
    imageBackground: colorsV2.purple20,
    cta: colorsV2.purple100,
    background: {
      left: colorsV2.purple10,
      right: colorsV2.purple20
    },
    icon: {
      borderColor: '#D5CBF6',
      shadowColor: '#AD9BEB',
      innerColor: '#5B37D2',
      outerColor: '#9880E5'
    }
  },
  red: {
    imageBackground: colorsV2.primary20,
    cta: colorsV2.primary100,
    background: {
      left: colorsV2.primary10,
      right: colorsV2.primary20
    },
    icon: {
      borderColor: '#FAD6D6',
      shadowColor: '#EC522B66',
      innerColor: '#E4421A',
      outerColor: '#FF8D70'
    }
  },
  green: {
    imageBackground: colorsV2.green20,
    cta: colorsV2.green100,
    background: {
      left: colorsV2.green10,
      right: colorsV2.green20
    },
    icon: {
      borderColor: '#CAECD1',
      shadowColor: '#347F43',
      innerColor: '#347F43',
      outerColor: '#4AB05E'
    }
  },
  yellow: {
    imageBackground: colorsV2.yellow20,
    cta: colorsV2.yellow100,
    background: {
      left: colorsV2.yellow10,
      right: colorsV2.yellow20
    },
    icon: {
      borderColor: '',
      shadowColor: '#FF991F66',
      innerColor: '#FE7E07',
      outerColor: '#FCAC00'
    }
  }
};

const Wrapper = styled(motion.div)`
  top: 64px;
  left: 0;
  right: 0;
  z-index: 99;
  background: linear-gradient(
    90deg,
    ${p => p.$color.left} 0%,
    ${p => p.$color.right} 100%
  );

  ${toScreen(desktopFirstV3.md)} {
    top: 66px;
  }

  .keen-slider {
    overflow: hidden;
  }

  .keen-slider__slide {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: stretch;
    width: 100%;
    min-width: 100%;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1144px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
`;

const ImageWrapper = styled(motion.div)`
  aspect-ratio: 96/90;
  position: relative;
  flex-shrink: 0;
  align-self: center;

  img {
    ${imageFitStyle('contain')}
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    aspect-ratio: 1/1;
    border-radius: 999px;
    background-color: ${p => p.$shapeColor};
    z-index: -1;

    .promotion & {
      transform: translateY(8px);
    }

    .compact & {
      display: none;
    }
  }
`;

const ContentWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 0;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CompactHighlight = styled.span`
  font-weight: normal;

  .compact & {
    font-weight: 700;
  }
`;

const IconWrapper = styled(motion.div)`
  position: absolute;
`;

const StyledLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  color: ${p => p.$color};

  &:hover {
    text-decoration: underline;
  }
`;

const NavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavigationButton = styled.button`
  width: 24px;
  height: 24px;
  background-color: transparent;
  cursor: pointer;
  border: none;
  padding: 0;
  margin: 0;

  svg {
    transition: opacity 0.4s ease-out;
    opacity: 0.4;
  }

  &:hover {
    svg {
      opacity: 1;
    }
  }
`;

const InboxDolButton = ({
  linkUrl = 'https://m.me/dolenglish.ieltsdinhluc',
  linkText = 'Nhắn cho DOL',
  color
}) => {
  return (
    <StyledLink
      $color={colorMapping[color].cta}
      href={linkUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Typography
        variant="medium/14-20"
        color={colorMapping[color].cta}
        type="span"
      >
        {linkText}
      </Typography>
      <RightArrowIcon size={14} color="currentColor" />
    </StyledLink>
  );
};

const getCoursePromotionContent = (item, color) => {
  const {
    value,
    remainSlot,
    initialTime,
    endDate,
    courseName,
    linkUrl,
    linkText
  } = item;

  const iconColor = colorMapping[color].icon;

  return {
    title: `Ưu đãi lên đến ${value}`,
    description: (
      <Description>
        <Typography variant="regular/14-20" color={colorsV2.neutral180}>
          <CompactHighlight>
            Còn <b>{remainSlot}</b> suất học bổng
          </CompactHighlight>
          , cho lớp <b>{courseName}</b> chương trình kết thúc sau{' '}
          <b>
            <Timer initialTime={initialTime} direction="backward">
              <Days />d <Hours />h <Minutes />m <Seconds />s
            </Timer>
            .&nbsp;
          </b>
          {linkUrl && (
            <InboxDolButton
              linkUrl={linkUrl}
              linkText={linkText}
              color={color}
            />
          )}
        </Typography>
        <Typography variant="medium/12-20" color={colorsV2.neutral180}>
          *Ưu đãi HÈ đăng ký trước {moment(endDate).format(MONTH_NAME_FORMAT2)}
        </Typography>
      </Description>
    ),
    color,
    imageHandle:
      courseName === 'IELTS'
        ? 'cma0jav0lij1w0714iy46lenj'
        : 'cma0jav18ijnd07zs8gbi578t',
    icon: <PromotionIcon {...iconColor} />,
    type: 'promotion'
  };
};

const AnimatedCompactTitle = ({ children, isCompact }) => {
  return (
    <AnimatePresence>
      {isCompact && (
        <motion.span
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            paddingRight: 4
          }}
          initial={{ width: 0, paddingRight: 0 }}
          animate={{ width: 'max-content' }}
          transition={transition}
        >
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  );
};

const getOtherItemsData = ({ isCompact }) => [
  {
    title: 'Sự kiện Đào tạo & Tập huấn',
    description: (
      <Typography variant="medium/14-20" color={colorsV2.neutral180}>
        <AnimatedCompactTitle isCompact={isCompact}>
          <b>Sự kiện Đào tạo.&nbsp;</b>
        </AnimatedCompactTitle>
        Linearthinking được <b>Bộ giáo dục</b> tổ chức tập huấn cho gần{' '}
        <b>400</b> giáo viên tiếng Anh THCS và THPT TP.HCM.{' '}
        <InboxDolButton
          linkUrl="/tin-tuc-ve-dol"
          linkText="Xem tin"
          color="green"
        />
      </Typography>
    ),
    color: 'green',
    imageHandle: 'cma0jav17ijnb07zsc1jjplns',
    icon: <TrainingIcon {...colorMapping.green.icon} />,
    type: 'training'
  },
  {
    title: 'Ra mắt nền tảng tự học ngữ pháp',
    description: (
      <Typography variant="medium/14-20" color={colorsV2.neutral180}>
        <AnimatedCompactTitle isCompact={isCompact}>
          Ra mắt nền tảng tự học&nbsp;<b>ngữ pháp.&nbsp;</b>
        </AnimatedCompactTitle>
        Tổng hợp <b>200+</b> điểm ngữ pháp tiếng Anh quan trọng kèm đầy đủ ví dụ
        minh họa và hơn <b>400+</b> bài tập.{' '}
        <InboxDolButton
          linkUrl="https://grammar.dolenglish.vn/"
          linkText="Xem thử"
          color="yellow"
        />
      </Typography>
    ),
    color: 'yellow',
    imageHandle: 'cma0vwxdglax007zs9e3p3xjb',
    icon: <PlatformIcon {...colorMapping.yellow.icon} />,
    type: 'platform'
  }
];

const PromotionContent = ({
  title,
  description,
  color,
  imageHandle,
  icon,
  isCompact,
  type
}) => {
  const { isMobile } = useDeviceDetect3();
  const getIconPosition = () => {
    switch (type) {
      case 'promotion':
        return {
          right: '-18%',
          top: '9%'
        };
      case 'training':
        return {
          right: '1%',
          top: '13%'
        };
      case 'platform':
        return {
          right: '-10%',
          top: '33%'
        };
      default:
        return {
          right: '-18%',
          top: '9%'
        };
    }
  };

  const iconPosition = getIconPosition();

  return (
    <ContentWrapper
      style={{ columnGap: isCompact ? 8 : isMobile ? 24 : 40 }}
      transition={transition}
      className={type}
    >
      <ImageWrapper
        $shapeColor={colorMapping[color].imageBackground}
        initial={{ height: 96, width: 'auto' }}
        animate={{ height: isCompact ? 60 : 96, width: 'auto' }}
        transition={transition}
      >
        <AnimatePresence>
          {!isCompact && (
            <motion.div
              key="image"
              initial={{ opacity: 1 }}
              animate={{ opacity: isCompact ? 0 : 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
            >
              <LazyImage handle={imageHandle} />
            </motion.div>
          )}
          {icon && (
            <IconWrapper
              key="icon-wrapper"
              initial={{
                right: iconPosition.right,
                top: iconPosition.top,
                x: 0,
                y: 0
              }}
              animate={{
                right: isCompact ? '50%' : iconPosition.right,
                top: isCompact ? '50%' : iconPosition.top,
                x: isCompact ? '50%' : 0,
                y: isCompact ? '-50%' : 0
              }}
              transition={transition}
            >
              {icon}
            </IconWrapper>
          )}
        </AnimatePresence>
      </ImageWrapper>
      <Content>
        <motion.div
          style={{ overflow: 'hidden' }}
          initial={false}
          animate={{ height: isCompact ? 0 : 'auto' }}
          transition={transition}
        >
          <Typography variant="semi-bold/16-24">{title}</Typography>
        </motion.div>
        {description}
      </Content>
    </ContentWrapper>
  );
};

const FixedTopBarScholarship = ({ data }) => {
  const [currentPromotionIndex, setCurrentPromotionIndex] = useState(0);
  const { y } = useWindowScroll();
  const { isDesktop, isMobile } = useDeviceDetect3();
  const isCompact = isDesktop ? y > 64 : y > 66;

  const [containerRef, slider] = useKeenSlider(
    {
      loop: true,
      slides: {
        perView: 1
      },
      slideChanged: slider => {
        setCurrentPromotionIndex(slider.track.details.rel);
      }
    },
    [slider => AutoPlay(slider, 5000)]
  );

  const list = React.useMemo(() => {
    return data.filter(item => {
      const { endDate } = item;
      const { remainSlot: remainQuantity } = item;
      const initialTime = Math.max(
        new Date(endDate).getTime() - new Date().getTime(),
        0
      );
      item.initialTime = initialTime;
      const isPromotionEmpty = remainQuantity === 0 || initialTime === 0;

      return !isPromotionEmpty;
    });
  }, [data]);

  const promotionCycle = ['purple', 'red'];
  const otherCycle = ['green', 'yellow'];
  const totalCycle = [...promotionCycle, ...otherCycle];
  const offset = Math.abs(list.length - otherCycle.length);
  const currentColor =
    totalCycle[(currentPromotionIndex % totalCycle.length) + offset];

  return (
    <Wrapper
      layout
      $color={colorMapping[currentColor].background}
      className={isCompact ? 'compact' : ''}
      style={{
        position: isCompact ? 'fixed' : 'sticky'
      }}
    >
      <Container>
        <div ref={containerRef} className="keen-slider">
          {list.map((item, index) => (
            <div className="keen-slider__slide" key={index}>
              <PromotionContent
                {...getCoursePromotionContent(item, currentColor)}
                isCompact={isCompact}
              />
            </div>
          ))}
          {getOtherItemsData({ isCompact, color: currentColor }).map(
            (item, index) => (
              <div className="keen-slider__slide" key={index}>
                <PromotionContent {...item} isCompact={isCompact} />
              </div>
            )
          )}
        </div>
        {!isMobile && (
          <NavigationWrapper>
            <NavigationButton onClick={() => slider.current?.prev()}>
              <IconArrowChevronLeftArrowLeft
                color={colorMapping[currentColor].cta}
              />
            </NavigationButton>
            <NavigationButton onClick={() => slider.current?.next()}>
              <IconArrowChevronLeftArrowRight
                color={colorMapping[currentColor].cta}
              />
            </NavigationButton>
          </NavigationWrapper>
        )}
      </Container>
    </Wrapper>
  );
};
export default FixedTopBarScholarship;
